<script setup>
import { ref, watch } from 'vue'
import { event } from 'vue-gtag'

//import {defineTableProps, pageTableProps} from './visualizations/table/GridTableSetup';
import {updateTableSettings, defineExportTableProps, fetchPageData}  from '@/components/explorer/visualizations/table/GridTableSetup';
import {ChartTypeEnum} from  "@/components/explorer/visualizations/ChartTypeEnum.js"

//events raised by component
const emit = defineEmits(['closeDownload'])

//peoperties set by parent
const props = defineProps({
	isOpen: {
		type: Boolean,
		default: false,
	},
	chartType: String,
	indicator: {
		type: Object,
		required: false,
	},
	chartObj: {
		type: Object,
		required: false,
	}
})

//reactive field proxies
const isDisabled = ref(true)
const downloadStarted = ref(false)
const imageTypes = ref([])
const tableTypes = ref([])
const isExporting = ref(false)
const progress = ref(0)
const statusMsg = ref('')
const statusTitle = ref('Downloading...')
const isError = ref(false)

imageTypes.value = [{name: 'JPG', label: 'JPEG', selected: false, format: 'image/jpeg'}, {name: 'PNG', label: 'PNG', selected: false, format: 'image/png'}, {name: 'PDF', label: 'PDF', selected: false, format: 'application/pdf'}]
tableTypes.value = [{name: 'CSV', label: "CSV", selected: false},{name: 'notes', label: 'Include data notes', selected: true}];

//methods
function closeModal() {
	downloadStarted.value = false
	isExporting.value = false;
	progress.value = 0;
	statusMsg.value = ''
	statusTitle.value = "Downloading..."
	isError.value = false
	isDisabled.value = false
	emit('closeDownload');
}

function showError(err) {
	downloadStarted.value = false
	console.log(err)
	statusMsg.value = "The file failed to download"
	statusTitle.value = "Error..."
	isError.value = true
	isDisabled.value = true
	event('error_download', { 'error' : err });
}

watch(() => props.isOpen, (value) => {
	if(value) {
		imageTypes.value.forEach(x => x.selected = false)
		tableTypes.value.forEach(x => x.name == 'notes'?x.selected = true: props.chartType == 'table'?x.selected = true:x.selected = false)
		isDisabled.value = true
		downloadStarted.value = false
		checkedType()
	}
});

function isNumber(value) {
	return typeof value === 'number';
}

function escapeCsvValue(label, value) {
	if (value === null || value === undefined) return '';

	if(isNumber(value))
		return value

	// Convert any type to string
	let str = String(value);

	// If it contains commas, quotes, or newlines → must be quoted
	const needsQuotes = /[",\n\r]/.test(str);

	if(label) {
		// Escape inner quotes by doubling them
		if (needsQuotes) {
			str = str.replace(/"/g, '""');
			return `"${label}: ${str}"`;
		} else {
			return `${label}: ${str}`;
		}
	} else {
	 	// Escape inner quotes by doubling them
	 	if (needsQuotes) {
	 		str = str.replace(/"/g, '""');
	 		return `"${str}"`;
	 	}
	 	return str;
	}
}

function startDownload() {
	const ts = new Date();
	downloadStarted.value = true
	let index = 0;
	isExporting.value = true
	progress.value = 0
	const selectedImages = imageTypes.value.filter(x => x.selected)
	const selectedTbls = tableTypes.value.filter(x => x.selected)
	let selectedTotalCount = selectedImages.length + selectedTbls.length

	async function exportNext() {
		//exit if user cancels dowload
		if(!downloadStarted.value)
			return

		if (index < selectedImages.length) {
			let filename = 'indicators_explorer_export_' + selectedImages[index].name + '_' + formatDateFilename(ts);
			try {
				props.chartObj.exportChartLocal({
					type: selectedImages[index].format,
					filename: filename,
					sourceWidth: 1920,
					sourceHeight: 1080,
					scale: 1
				})
				event('download_chart', { 'indicator' : props.indicator.indicatorName, 'download_type': selectedImages[index].format});
				index++;
				progress.value = Math.round((index /selectedTotalCount) * 100)
				statusMsg.value = `${index} of ${selectedTotalCount} files downloaded`
				setTimeout(() => {
						exportNext();
				}, 1500);
			} catch (err) {
				showError(err)
				return;
			}
		} else {
			//export table after all charts have been exported
			//if (index >= selectedImages.length) {
			if(selectedTbls.length > 0) {
				//This CSV export is from chart object so use different method than exporting from a grid table view
				for(let x =0; x < selectedTbls.length;x++) {
					if(selectedTbls[x].name == 'CSV') {
						//(async () => {
							await exportTableData()
							event('download_chart', { 'indicator' : props.indicator.indicatorName, 'download_type': 'csv'});
						//})()
					} else if(selectedTbls[x].name == 'notes') {
						exportNotes()
						event('download_chart', { 'indicator' : props.indicator.indicatorName, 'download_type': 'notes'});
					}
				}
				closeModal();
				return;
			} else {
				closeModal();
				return;
			}

		}
	}
	exportNext();
}

function exportNotes() {
	const notes = props.indicator.notes;
	const abbrs = props.indicator.abbreviations !=null && props.indicator.abbreviations.length > 0?props.indicator.abbreviations.join('; '):'';
	const src = props.indicator.source;
	const indName = props.indicator?.indicatorName
	const topic = props.indicator?.topicName
	const subtopic = props.indicator?.subtopicName
	const baseUrl = import.meta.env.VITE_BASE_URL

	const ts = new Date();
	const downloadDate = formatTimestamp(ts)


	let filters = ''
	props.indicator.columns.items.forEach(i => {
		if(i.valueFlag == 0 && i.categoryLabelFlag == 1) {
			if(i.appliedValues && i.appliedValues.length > 0) {
				let values = i.appliedValues.filter(x => x.selected)
				if(values.length < i.appliedValues.length) {
					//footerNotes += i.columnName + ": " + val.name + "; "
					let selected = i.columnName + ": "
					values.forEach((x, indx) => {
						selected += x.name + (indx < values.length-1?', ':'')
					})
					filters += selected + ";\n"
				}
			}
		}
	})

	//ensure text is using UTF-8 char set
	const BOM = '\uFEFF';
	//const blob = new Blob([BOM + notes], { type: 'text/plain;charset=utf-8;' });
	const blob = new Blob([
		BOM,
		indName,
		'\n',
		'Available at: ' + baseUrl,
		'\n',
		'Topic: ' + topic,
		'\n',
		'Subtopic: ' + subtopic,
		'\n',
		'Source: ' +  (src== null?'No source':src),
		'\n',
		'Download date: ' + downloadDate,
		'\n\n',
		(filters.length > 0?'Settings: \n':''),
		(filters.length > 0?filters + '\n':''),
		(abbrs.length > 0?'Key: \n':''),
		(abbrs.length > 0?abbrs + '\n\n':''),		
		'Data Notes: ',
		'\n',		
		notes == null?'No notes':notes], { type: 'text/plain;charset=utf-8;' });
	const url = URL.createObjectURL(blob);

	const link = document.createElement('a');
	link.href = url;
	link.download = 'indicators_explorer_export_metadata_' + formatDateFilename(ts) + '.txt';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

function formatDateFilename(d) {
	let mm = '0' + (d.getMonth() + 1),
		dd = '0' + d.getDate(),
		yy = d.getFullYear();
	let hh = '0' +(d.getHours() + 1),
		mi = '0' + (d.getMinutes() + 1);

	return [yy, mm.slice(-2), dd.slice(-2)].join('') + '_' + [hh.slice(-2), mi.slice(-2)].join('');
}

function formatTimestamp(d) {
	let mm = '0' + (d.getMonth() + 1),
		dd = '0' + d.getDate(),
		yy = d.getFullYear();
	let hh = '0' +(d.getHours() + 1),
		mi = '0' + (d.getMinutes() + 1);

	return [mm.slice(-2), dd.slice(-2), yy].join('/') + ' ' + [hh.slice(-2), mi.slice(-2)].join(':');
}

async function exportTableData() {
	isExporting.value = true
	progress.value = 0

	let allData = []

	const exportSettings = {};
	exportSettings.request = {};
	const supportsMaps = props.indicator.chartTypes.includes('Map')
	updateTableSettings(exportSettings, props.indicator, ChartTypeEnum.TABLE.chartName)
	//console.log('exportSettings', exportSettings)
	await defineExportTableProps(props.indicator, exportSettings)

	//Remove parent from table if it exists in returned data
	let parentCol = exportSettings.allDims.find(p => p.childColumnName != null)

	for (let page = 0; page < exportSettings.totalPages; page++) {
		//exit if user cancels dowload
		if(!downloadStarted.value)
			return

		exportSettings.currentPage = page
		const results = await fetchPageData(exportSettings)
		let cols = {}

		// Add values or values, notes as last columns
		const notesArr = results["Notes"]
		const valueArr = results[exportSettings.valueCol]

		let geoCol = null
		let geoFields = []
		let geoColArrays = []
		let geoColAdded = false
		if(supportsMaps) {
			const keys = Object.keys(results)
			geoFields = keys.filter(x => x == "hc-key" ||x == "iso2" ||x == "iso3" || x == "state-fips" || x == "State FIPS" || x == "FIPS")
			//geoField = keys.find(x => x == "iso2")

			if(geoFields.length > 0) {
				geoCol = props.indicator.columns.items.find(y => y.geoLabelFlag == 1)
				geoFields.forEach(x => {
					geoColArrays.push({name: x, data: results[x]})
				})
			}
		}


		//Add other columns
		for(let x=0; x < exportSettings.allDims.length; x++) {
			const tmpKey = exportSettings.allDims[x].columnName
			if(valueArr && tmpKey == exportSettings.valueCol)
				continue
			else if(notesArr && tmpKey == "Notes")
				continue
			else if(geoColArrays.length > 0 && geoFields.includes(tmpKey))
				continue
			else if(parentCol != null && parentCol.columnName == tmpKey)
				continue

			const tmpArr = results[tmpKey]
			if(tmpArr != null)
				cols[tmpKey] = Object.values(tmpArr)

			//insert geo data after the map geo column identified inmetadata
			if(supportsMaps && geoColArrays.length > 0) {
				if(geoCol && tmpKey == geoCol.columnName){
					geoColAdded = true
					geoColArrays.forEach(x => {
						cols[x.name] = Object.values(x.data)
					})

				}
			}
		}

		if(valueArr != null){
			cols[exportSettings.valueCol] = Object.values(valueArr)
		}
		if(notesArr != null){
			cols["Notes"] = Object.values(notesArr)
		}

		//if geo field not identified in metadata then append after notes
		if(supportsMaps && geoColAdded == false && geoColArrays.length > 0) {
			geoColArrays.forEach(x => {
				cols[x.name] = Object.values(x.data)
			})
		}
		allData.push(... convertObjectToRows(cols))

		// update progress %
		progress.value = Math.round((page / exportSettings.totalPages) * 100)
		statusMsg.value = `${(exportSettings.exportPageSize * page)} of ${exportSettings.totalItems} rows downloaded`
	}

	// Build CSV
	let sheetRows, header = [];

	const indName = escapeCsvValue("",props.indicator?.indicatorName)
	const topic = escapeCsvValue("Topic",props.indicator?.topicName)
	const subtopic = escapeCsvValue("Subtopic",props.indicator?.subtopicName)
	const src = escapeCsvValue("Source", props.indicator?.source)
	const abbr = props.indicator.abbreviations !=null && props.indicator.abbreviations.length > 0 ? 'Key: ' + props.indicator.abbreviations.join('; ') + '.' : '';

	header.push(indName)
	header.push(topic)
	header.push(subtopic)
	header.push(src)
	if(abbr.length > 0) header.push(abbr)
	header.push('')

	//create 2 instances of header names, one to lookup data, a second with any formatting needed for display
	const columnDataNames = Object.keys(allData[0] || {})
	const columnDisplayNames = Object.keys(allData[0] || {})
	//append rounding unit to value header name
	if(props.indicator?.roundingUnit) {
		let indx = columnDisplayNames.findIndex(x => x == exportSettings.valueCol)
		if(indx > -1) {
			columnDisplayNames[indx] = exportSettings.valueCol + ' (' + props.indicator.roundingUnit + ')'
		}
	}

	//Use the header containing rounding unit as the csv header, then use the original datra header to lookup row cells for formatting
	const csvRows = [columnDisplayNames.join(',')]
	for (const row of allData) {
		const csvRow = columnDataNames.map(col => escapeCsvValue("", row[col]))
		csvRows.push(csvRow.join(','))
	}
	sheetRows = header.concat(csvRows)

	const ts = new Date();
	const csvContent = sheetRows.join('\n')
	const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
	const link = document.createElement('a')
	link.href = URL.createObjectURL(blob)
	link.download = 'indicators_explorer_export_data_' + formatDateFilename(ts) + '.csv';
	link.click()
	return;
}

function convertObjectToRows(dataObj) {
  // Get property names (columns)
  const keys = Object.keys(dataObj);

  // Assume all arrays are same length
  const rowCount = dataObj[keys[0]].length;

  const rows = [];

  for (let i = 0; i < rowCount; i++) {
    const row = {};
    for (const key of keys) {
      row[key] = dataObj[key][i];
    }
    rows.push(row);
  }

  return rows;
}

function checkedType() {
	const selectedImages= imageTypes.value.filter(x => x.selected)
	const selectedTbls= tableTypes.value.filter(x => x.selected)
	if(selectedImages.length > 0 || selectedTbls.length > 0)
		isDisabled.value = false
	else
		isDisabled.value = true
}

function disableImage() {
	if(props.chartType == "table")
		return true;
	else
		return false;
}
</script>

<template>
	<div v-if="props.isOpen" class="modal-overlay">
		<div class="modal-container">
			<div class="modal-header">
				Download
				<button class="btn-icon btn-close" @click="closeModal" aria-label="Close"><i class="fa-solid fa-times"></i></button>
			</div>
			<div class="modal-body">
				<div v-show="!isExporting" class="selection-container">
					<div>
						<div class="title">
							Chart Type
						</div>
						<div id="chartTitle" class="sub-title">{{props.chartType !=null?props.chartType.charAt(0).toUpperCase() + props.chartType.slice(1):''}} </div>
					</div>
					<hr />
					<div class="image-types">
						<div class="title">
							Image Formats
						</div>
						<ul id="imagerFormats">
							<li v-for="type in imageTypes" :key="type.name">
								<label>
									<input type="checkbox" v-model="type.selected" @change="checkedType" :disabled="disableImage()">
									<div class="fld-value" :class="{'disabled':disableImage() == true}">{{ type.label }}</div>
								</label>
							</li>
						</ul>
					</div>
					<hr />
					<div class="image-types">
						<div class="title">
							Table Formats
						</div>
						<ul id="tableFormats">
							<li v-for="type in tableTypes" :key="type.name">
								<label>
									<input type="checkbox" v-model="type.selected" @change="checkedType">
									<span class="fld-value">{{ type.label }}</span>
								</label>
							</li>
						</ul>
					</div>
				</div>
				<!-- Progress bar -->
				<div v-if="isExporting">
					<div class="text-info">
						Downloading this data may take a few moments. If you experience a wait time longer than one minute,
						please contact us at <span class="link">ncsesdata@nsf.gov</span>.
					</div>
					<div class="text-info sub-title" :class="{error: isError}">
						{{statusTitle}}
					</div>
					<div  class="progress-bar">
						<div class="progress-fill" :class="{error: isError}" :style="{ width: progress + '%' }"></div>
					</div>
					<div class="info-text" :class="{error: isError}">
						{{statusMsg}}
					</div>
				</div>
				<div class="modal-footer">
					<button class="btn-secondary" @click="closeModal" v-show="!downloadStarted">Cancel</button>
					<button class="btn-primary" @click="startDownload" v-show="!downloadStarted" :disabled="isDisabled">Download</button>

					<button class="btn-primary" @click="closeModal" v-show="downloadStarted">Cancel Download</button>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index:100
}

.modal-container {
	background-color: var(--surface-color-Default);
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content:flex-start;
	max-width: 48.25rem;
	width: 100%;
	min-height: 33rem;
	padding: var(--spacing-0, 0rem);
	gap: var(--spacing-3xl, 2.5rem);
	position: relative;
	z-index: 101;
    border-top-left-radius: var(--radius-xl);
    border-top-right-radius: var(--radius-xl);
    border-bottom-left-radius: var(--radius-xl);
    border-bottom-right-radius: var(--radius-xl);
}

.selection-container {
	height: 100%;
	width: 100%;
}

.modal-header {
	display: flex;
	height: 5rem;
	padding: 1.4375rem 2rem 1.5625rem 2rem;
	justify-content: space-between;
	align-items: center;
	align-self: stretch;
	border-bottom: 1px solid var(--borders-DividerActive, #064291);
	background: var(--surface-brand-Blue, #064291);
	color: var(--text-Inverse, #fff);
	font-family: var(--font-family-Title);
	font-size: var(--font-size-H5);
    border-top-left-radius: var(--radius-xl);
    border-top-right-radius: var(--radius-xl);
}

.modal-body {
	background: var(--surface-Default, #FFF);
	min-height: 15.0625rem;
	display: flex;
	padding: 0rem var(--spacing-xl, 1.5rem) var(--spacing-xl, 6rem) var(--spacing-xl, 1.5rem);
	flex-direction: column;
	align-items: flex-start;
	gap: var(--spacing-xl, 1.5rem);
	align-self: stretch;
}

.modal-footer {
	display: flex;
	padding: var(--spacing-lg, 1rem) var(--spacing-xl, 1.5rem);
	justify-content: flex-end;
	align-items: flex-end;
	gap: var(--spacing-lg, 1rem);
	align-self: stretch;
	max-height: 5rem;

	border-top: 1px solid var(--border-color-Divider, #A9B7C7);
	background: var(--surface-default, #FFF);
    border-bottom-left-radius: var(--radius-xl);
    border-bottom-right-radius: var(--radius-xl);
}

.title {
	color: var(--text-secondary, #757575);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-H6);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */
}

.sub-title {
	color: var(--text-Disabled);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */
	margin-top: var(--spacing-lg);
	display: block;
}

.text-info {
	color: var(--text-Primary);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1rem);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base, 1.5rem); /* 150% */
	display: block;
}

.text-info.sub-title{
	font-weight: 500;
	margin-top: 1rem;
}

ul {
	list-style-type: none;
    padding-left: 0;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
    column-gap: var(--spacing-xl);
}

li {
	text-wrap: nowrap;
    align-items: center;
    display: flex;
}

#imagerFormats label,
#tableFormats label {
	display: flex;
	align-items: center
}

.fld-value {
	padding-left: .25rem;
}

.fld-value.disabled {
	color: var(--text-Disabled);
}

input[type='checkbox'] {
	height:1.5rem;
	width:1.5rem;
}

hr {
	width: 100%;
	color: var(--border-color-Divider);
	margin: 2rem 0;
}
.btn-close {
	width: 1rem;
	color: var(--icons-icon-cta-reverse, #FFF);
	background: var(--surface-brand-Blue, #064291);
	font-size: 1.25rem;
	font-style: normal;
	font-weight: 900;
	line-height: normal;
	border: 0;
}

.btn-secondary {
	margin-right: var(--spacing-md);
}

.link {
	color: var(--text-Link);
}

.progress-bar {
  position: relative;
  margin: 1rem 0;
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #2563eb;
  transition: width 0.2s ease;
}

.error {
	color: var(--text-Error);
}

.progress-fill.error {
	background-color: var(--text-Error);
}

/* 499px - Mobile */
@media (max-width: 31.1875rem) {

	.modal-overlay {
		position: absolute;
		top: 8.4375rem; /*  135px; */
		left: 0;
		width: calc(100vw);
		height: calc(100vh);
	}


	.modal-container {
		width: 100%;
		height: 100%;
		justify-content: start;
		position: absolute;
		top: 0;
		border-radius: 0;
	}

	.modal-header {
		border-radius: 0;
	}
}
</style>
